import './AutosuggestResultItem.scss'

import { HDSButton, HDSIcon, HDSTooltip } from '@here/hds-react-components'
import classNames from 'classnames'
import ListItem from 'components/App/components/ListItem'
import SvgPdsIcon from 'components/Map/components/SvgPdsIcon'
import { SearchStateListType } from 'data/model/search'
import { PageType, useNavigation } from 'lib/hooks'
import { i18n } from 'lib/i18n'
import React, { BaseSyntheticEvent, MouseEvent, useCallback, useState } from 'react'

import SearchResultItemDetails from '../SearchResultItemDetails'

export interface AutosuggestEvent {
  id: string
  title: string
  nativeEvent: BaseSyntheticEvent
}

export interface AutosuggestResultItemProps {
  id: string
  iconName?: string
  categoryId?: string
  kind?: string
  link: string
  title: string
  highlightedSubtitle?: string
  subtitle?: string
  onClick: (event: AutosuggestEvent) => void
  onDeleteItem?: (e: MouseEvent, id: string) => void
  onMouseEnter?: (id: string) => void
  onMouseLeave?: (id: string) => void
}

const AutosuggestIcon = ({
  iconName,
  categoryId,
}: {
  iconName: string | undefined
  categoryId: string | undefined
}): JSX.Element => {
  return (
    <>
      {categoryId !== undefined ? (
        <SvgPdsIcon className="AutosuggestResultItem__icon" categoryId={categoryId} />
      ) : (
        <div className="AutosuggestResultItem__icon">
          <HDSIcon
            name={iconName !== undefined ? iconName : 'point'}
            category={iconName !== undefined ? 'core-ui' : 'poi'}
            size="18"
            style={{ '--hds-icon-color': 'var(--hds-poi-focus-generic)' } as React.CSSProperties}
          />
        </div>
      )}
    </>
  )
}

export default function AutosuggestResultItem({
  id,
  iconName,
  categoryId,
  kind,
  link,
  title,
  highlightedSubtitle,
  subtitle,
  onClick,
  onDeleteItem,
  onMouseEnter,
  onMouseLeave,
}: AutosuggestResultItemProps): JSX.Element {
  const [isHighlighted, setIsHighlighted] = useState<boolean>(false)
  const { goTo } = useNavigation()

  const handleMouseEnter = useCallback((): void => {
    setIsHighlighted(true)
    onMouseEnter !== undefined && onMouseEnter(id)
  }, [id, onMouseEnter])

  const handleMouseLeave = useCallback((): void => {
    setIsHighlighted(false)
    onMouseLeave !== undefined && onMouseLeave(id)
  }, [id, onMouseLeave])

  const handleClick = useCallback(
    (event: BaseSyntheticEvent) => {
      const pageType = link.startsWith('/discover/') ? PageType.DISCOVERY : PageType.PDC

      onClick({ id: id, nativeEvent: event, title: title })
      goTo({ type: pageType, url: link })
    },
    [id, onClick, title, goTo, link]
  )

  const linkClassNames = classNames('AutosuggestResultItem__link', {
    'AutosuggestResultItem__link--highlighted':
      isHighlighted && kind === SearchStateListType.Recent,
  })
  const linkChildren = (
    <>
      <div className="AutosuggestResultItem__icon-container">
        <AutosuggestIcon iconName={iconName} categoryId={categoryId} />
      </div>
      <SearchResultItemDetails
        title={title}
        subtitle={subtitle}
        highlightedSubtitle={highlightedSubtitle}
      />
    </>
  )

  // an icon container is always shown (even when there's no icon) to have consistent offset
  return (
    <ListItem
      className="AutosuggestResultItem"
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={linkClassNames}>{linkChildren}</div>
      {isHighlighted && kind === SearchStateListType.Recent && (
        <HDSTooltip position="bottom" content={i18n('search.recent.delete')}>
          <HDSButton
            className="SearchResultItem__delete"
            variant="subtle"
            size="small"
            icon="cross"
            onMouseDown={(e) => onDeleteItem && onDeleteItem(e, id)}
          />
        </HDSTooltip>
      )}
    </ListItem>
  )
}
